import { format, isBefore, isSameDay, startOfToday } from "date-fns";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { AvailabilityHeatmapDay } from "@/types/scheduling/schedule";

interface AvailabilityDateButtonProps {
  availability?: AvailabilityHeatmapDay;
  date: Date;
  disabled?: boolean;
  isLoading?: boolean;
  onSelectDate: (date: Date) => void;
  selectedDate: Date;
}

export default function AvailabilityDateButton({
  availability,
  date,
  disabled = false,
  isLoading = false,
  onSelectDate,
  selectedDate,
}: AvailabilityDateButtonProps) {
  const { t } = useTranslation();
  const availabilityDescriptionId = `${useId()}-availability`;
  const dateLabel = format(date, "EEEE, MMMM d, yyyy");
  const isSelected = isSameDay(date, selectedDate);
  const isBeforeToday = isBefore(date, startOfToday());

  if (
    isLoading ||
    !availability ||
    availability.total_time_slots === 0 ||
    isBeforeToday
  ) {
    return (
      <button
        disabled
        aria-label={dateLabel}
        aria-describedby={isLoading ? undefined : availabilityDescriptionId}
        onClick={() => {
          onSelectDate(date);
        }}
        className={cn(
          "h-full w-full hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg relative overflow-hidden cursor-not-allowed dark:bg-gray-900",
          isSelected ? "ring-2 ring-primary-500" : "",
        )}
      >
        <div className="relative z-10 flex h-full flex-col justify-between p-1.5 text-left md:p-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-50 md:text-base">
            {date.getDate()}
          </span>
          {!isLoading && (
            <span
              id={isLoading ? undefined : availabilityDescriptionId}
              className="text-[9px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 md:text-[10px]"
            >
              --
            </span>
          )}
        </div>
      </button>
    );
  }

  const { booked_slots, total_slots, total_time_slots, available_time_slots } =
    availability;
  const bookedPercentage =
    Number.isFinite(total_slots) && total_slots > 0
      ? booked_slots / total_slots
      : 0;
  const slotOccupancy =
    Number.isFinite(total_time_slots) && total_time_slots > 0
      ? 1 - available_time_slots / total_time_slots
      : bookedPercentage;
  const isFullyBooked = available_time_slots <= 0;

  return (
    <button
      disabled={disabled || isBeforeToday || isFullyBooked}
      aria-label={dateLabel}
      aria-describedby={availabilityDescriptionId}
      onClick={() => {
        onSelectDate(date);
      }}
      className={cn(
        "h-full w-full hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md relative overflow-hidden border hover:scale-105 hover:shadow-md transition-all dark:bg-gray-900",
        isSelected
          ? "border-2 border-primary-600 bg-green-50 hover:bg-green-50"
          : "border-gray-400",
        isFullyBooked && "bg-gray-200",
        disabled && "cursor-not-allowed",
      )}
    >
      {isSelected && (
        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-6 h-1 rounded-b-sm bg-primary-600 z-20" />
      )}
      <div className="relative z-10 flex h-full flex-col justify-between p-1.5 text-left md:p-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-50 md:text-base">
          {date.getDate()}
        </span>
        <div
          id={availabilityDescriptionId}
          className="flex flex-col gap-0.5 leading-none"
        >
          <span
            className={cn(
              "text-sm font-semibold md:text-base",
              isFullyBooked
                ? "text-gray-500"
                : slotOccupancy >= 0.8
                  ? "text-red-600"
                  : slotOccupancy >= 0.5
                    ? "text-yellow-600"
                    : "text-primary-600",
            )}
          >
            {available_time_slots}
          </span>
              <span className="text-[9px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 md:text-[10px]">
            {t("available")}
          </span>
        </div>
      </div>
      {!isFullyBooked && (
        <div
          className={cn(
            "absolute bottom-0 left-0 w-full transition-all",
            slotOccupancy > 0.8
              ? "bg-red-100"
              : slotOccupancy > 0.5
                ? "bg-yellow-100"
                : "bg-primary-100",
          )}
          style={{ height: `${Math.min(slotOccupancy * 100, 100)}%` }}
        />
      )}
    </button>
  );
}
